import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { shallowEqual, useSelector } from "react-redux";
import wrapperHOC from "@Utils/wrapperHOC";
import { AppState } from "@Store/index";
import Modal from "@Components/Modal";
import Portal from "@Components/Portal";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { EVENTS } from "@Pages/List/Desktop/constants";
import ListWithUsForm from "@Pages/List/Desktop/components/ListWIthUsForm/ListWithUsForm";
import V2Footer from "@Components/V2Footer/V2Footer";
import Header from "./components/Header/Header";
import Hero from "./components/Hero/Hero";
import GlobeTravel from "./components/GlobeTravel/GlobeTravel";
import WhyPartners from "./components/WhyPartners/WhyPartners";
import Features from "./components/Features/Features";
import Steps from "./components/Steps/Steps";
import Tools from "./components/Tools/Tools";
import Audience from "./components/Audience/Audience";
import Testimonials from "./components/Testimonials/Testimonials";
import CaseStudies from "./components/CaseStudies/CaseStudies";
import Faq from "./components/Faq/Faq";
import Cta from "./components/Cta/Cta";
import styles from "./ListWithUsV2.module.scss";
import useTranslationHook from "hooks/useTranslationHook";

/**
 * Behaviour carried over from the page this replaces (pages/List/List.tsx) — the
 * design supplied the layout, not the wiring, so every piece below is the old
 * page's, kept deliberately:
 *
 *   · a partner who is already signed in and has dashboard access never sees the
 *     marketing page at all; they are sent straight to amber connect
 *   · returning from the login modal sets `#loggedIn`, which also redirects
 *   · the CTAs open the existing listing form in a full-screen modal — they are
 *     not links, and there is no page to navigate to
 *   · the same three gtag events fire: landing, first scroll, and CTA click
 */
const ListWithUsV2 = () => {
  const { t } = useTranslationHook();
  const { isUserLoggedIn, hasPartnerDashboardAccess } = useSelector(
    ({ appData }: AppState) => ({
      isUserLoggedIn: appData.isUserLoggedIn,
      hasPartnerDashboardAccess: appData.hasPartnerDashboardAccess,
    }),
    shallowEqual,
  );
  const [showForm, setShowForm] = useState(false);
  const [isScrollCaptured, setIsScrollCaptured] = useState(false);

  const toggleFormState = () => setShowForm((prev) => !prev);

  const handleCTAClick = () => {
    gtagEventEmitter({ action: EVENTS.ctaBtnClick, category: EVENTS.category });
    toggleFormState();
  };

  const handleScroll = () => {
    if (isScrollCaptured) return;
    gtagEventEmitter({ action: EVENTS.pageScroll, category: EVENTS.category });
    setIsScrollCaptured(true);
  };

  // ⚠️  NO REDIRECT HERE ANY MORE, BY DESIGN.
  //
  // This used to send a signed-in partner to /connect on mount, mirroring the SSR
  // redirect in appReducer's authMiddlewareServer. Both are gone: a partner is now
  // OFFERED the dashboard — `Header` and the hero swap their CTA to "Go to Dashboard"
  // off `hasPartnerDashboardAccess` — rather than being moved there.
  //
  // The `hashchange` listener that sat here went with them. It watched for `#loggedIn`
  // on the reasoning that a partner returning from the login modal should land in the
  // dashboard; nothing in the codebase ever set that hash (`LOGIN_SUCCESS_HASH` is
  // declared in components/Login/contants.ts and referenced nowhere), so it never fired.
  // Signing in on this page reloads it (see components/Login/helpers/loginSuccess.ts),
  // and the reloaded page shows the partner their dashboard CTA.
  useEffect(() => {
    gtagEventEmitter({ action: EVENTS.onLandingPage, category: EVENTS.category });
  }, []);

  return (
    <div className={styles.page} onScroll={handleScroll}>
      <Helmet title={t("list.v2.meta.title")}>
        <meta name="description" content={t("list.v2.meta.description")} />
        {/* Instrument Sans is the design font; Geist / Geist Mono are used by the
          amber connect mock-ups. Loaded only on this page. */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&family=Geist:wght@400;500;600&family=Geist+Mono:wght@400;500;600&display=swap"
        />
      </Helmet>

      <Header onListClick={handleCTAClick} />
      {/* Hero also carries the partner logo wall — see Figma node 2483:9807. */}
      {/* The globe lives here, not inside a section: it is a single fixed layer that
        parks over the hero's slot and glides into the "Why partners" slot on scroll.
        See GlobeTravel for why there is only one instance. */}
      <GlobeTravel />

      <Hero onListClick={handleCTAClick} isPartner={isUserLoggedIn && hasPartnerDashboardAccess} />
      {/* ⚠️  EVERYTHING BELOW THE HERO SITS IN ONE STACKING CONTEXT ABOVE THE GLOBE, and that is what
        lets the hero reserve only as much height as the shrunken globe needs.

        The globe is a `position: fixed` layer at z-index 1, so it paints over static content —
        which meant the hero's slot had to reserve enough room that the shrinking globe could never
        reach the next section, and the difference between that reserve and the shrunken globe was
        dead white space. Raising this block above the layer removes the constraint: the globe slides
        UNDER the content as the page scrolls, so the reserve can match the shrunken size exactly.

        The background is load-bearing too — without it the globe would show through the gaps between
        sections that do not set one of their own. */}
      <div className={styles.belowHero}>
        <WhyPartners />
        {/* The five things a partner gets, on the same card rail Partner With Us uses. Sits here
          rather than lower down because it answers the questions "Why partners list with amber"
          raises, before the page moves on to how listing works. */}
        <Features />
        <Steps />
        <Tools />
        <Audience />
        {/* `Video` — "Begin your journey with amber", the Grow with us player — was here,
          between Audience and Testimonials. Removed from the page; the component is still in
          `components/Video/` with its assets, so putting it back is this line plus its import. */}
        <Testimonials />
        {/* Split out of Testimonials — see the note at the top of that file. */}
        <CaseStudies />
        <Faq />
        {/* The "Sign up now" band is for visitors: anyone signed in has already signed up. */}
        {!isUserLoggedIn && <Cta onListClick={handleCTAClick} />}
        <V2Footer />
      </div>

      {/* The listing form, in the same full-screen modal the old page used. Mounted
          only while open, so nothing of it is requested until someone asks for it. */}
      {showForm && (
        <Portal rootId="forPortal">
          <Modal hideModal={toggleFormState} size="fullScreen" animation="noAnimate">
            <ListWithUsForm closeForm={toggleFormState} />
          </Modal>
        </Portal>
      )}
    </div>
  );
};

export default wrapperHOC(ListWithUsV2, {
  componentName: "ListWithUsV2-Page",
  showForChina: true,
});
